export function mean(values: readonly number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function median(values: readonly number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) {
    return sorted[mid] ?? null;
  }
  const left = sorted[mid - 1];
  const right = sorted[mid];
  if (left === undefined || right === undefined) {
    return null;
  }
  return (left + right) / 2;
}

export function maximumDrawdown(cumulativePnl: readonly number[]): number {
  let peak = Number.NEGATIVE_INFINITY;
  let maxDd = 0;
  for (const value of cumulativePnl) {
    peak = Math.max(peak, value);
    maxDd = Math.max(maxDd, peak - value);
  }
  return maxDd;
}

export function quantile(values: readonly number[], p: number): number | null {
  if (values.length === 0) {
    return null;
  }
  if (!(p >= 0) || p > 1 || !Number.isFinite(p)) {
    throw new Error('Quantile p must be a finite number in [0, 1].');
  }
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 1) {
    return sorted[0] ?? null;
  }
  const index = p * (sorted.length - 1);
  const low = Math.floor(index);
  const high = Math.ceil(index);
  const left = sorted[low];
  const right = sorted[high];
  if (left === undefined || right === undefined) {
    return null;
  }
  if (low === high) {
    return left;
  }
  const weight = index - low;
  return left * (1 - weight) + right * weight;
}

export function stddev(values: readonly number[]): number | null {
  if (values.length < 2) {
    return null;
  }
  const avg = mean(values);
  if (avg === null) {
    return null;
  }
  const squared = values.reduce((sum, value) => sum + (value - avg) ** 2, 0);
  return Math.sqrt(squared / (values.length - 1));
}

export interface ModeResult {
  values: number[];
  frequency: number;
  meaningful: boolean;
}

/** Most frequent value(s). Meaningful only if the top frequency is at least 2. */
export function mode(values: readonly number[]): ModeResult | null {
  if (values.length === 0) {
    return null;
  }
  const counts = new Map<number, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  let topFrequency = 0;
  for (const frequency of counts.values()) {
    topFrequency = Math.max(topFrequency, frequency);
  }
  const modes = [...counts.entries()]
    .filter(([, frequency]) => frequency === topFrequency)
    .map(([value]) => value)
    .sort((left, right) => left - right);
  return {
    values: modes,
    frequency: topFrequency,
    meaningful: topFrequency >= 2,
  };
}

export function proportion(numerator: number, denominator: number): number | null {
  if (denominator <= 0) {
    return null;
  }
  return numerator / denominator;
}
